import { CaseMilestone, isOptionalMilestone } from '@/hooks/useCaseMilestones';
import { type StatusCategory } from '@/utils/statusCategoryMap';

interface CaseProgressBarProps {
  milestones: CaseMilestone[];
  /** Negociação cancelada: barra em vermelho e congelada */
  isCancelled?: boolean;
}

const STEP_COLORS: Record<StatusCategory, { bar: string; label: string; pulse?: boolean }> = {
  completed:       { bar: 'bg-emerald-500',  label: 'text-emerald-600 dark:text-emerald-400' },
  internal_action: { bar: 'bg-amber-400',    label: 'text-amber-600 dark:text-amber-400', pulse: true },
  external_wait:   { bar: 'bg-blue-400',     label: 'text-blue-500 dark:text-blue-400' },
  action_required: { bar: 'bg-rose-500',     label: 'text-rose-600 dark:text-rose-400', pulse: true },
  not_started:     { bar: 'bg-border',       label: 'text-muted-foreground' },
  failed:          { bar: 'bg-red-600',      label: 'text-red-600 dark:text-red-400' },
};

export default function CaseProgressBar({ milestones, isCancelled = false }: CaseProgressBarProps) {
  if (milestones.length === 0) return null;

  return (
    <div className={`rounded-xl px-5 py-4 shadow-sm border ${isCancelled ? 'bg-destructive/5 border-destructive/40' : 'bg-card border-border'}`}>
      {isCancelled && (
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-destructive">
          Cancelado
        </div>
      )}
      <div className={`flex flex-wrap gap-y-1.5 items-center ${isCancelled ? 'opacity-80' : ''}`}>
        {milestones.map((m, i) => {
          const category = (m.status_category || 'not_started') as StatusCategory;
          const colors = isCancelled
            ? { bar: 'bg-destructive', label: 'text-destructive', pulse: false }
            : STEP_COLORS[category];
          const isFirst = i === 0;
          const isLast = i === milestones.length - 1;
          // Etapa opcional ainda não usada fica apagada: não bloqueia a conclusão.
          const isIdleOptional = category === 'not_started' && isOptionalMilestone(m);
          const arrow = 10;

          const clipPath = isFirst
            ? `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%)`
            : isLast
              ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${arrow}px 50%)`
              : `polygon(0 0, calc(100% - ${arrow}px) 0, 100% 50%, calc(100% - ${arrow}px) 100%, 0 100%, ${arrow}px 50%)`;

          return (
            <div
              key={m.id}
              style={{ clipPath, marginLeft: isFirst ? 0 : -(arrow - 2) }}
              title={isIdleOptional ? `${m.name} (opcional)` : m.name}
              className={`flex-1 min-w-[80px] h-8 flex items-center justify-center ${colors.bar} ${
                colors.pulse ? 'animate-pulse' : ''
              } ${isIdleOptional ? 'opacity-40' : ''}`}
            >
              <span
                className={`text-[10px] font-semibold uppercase tracking-wide truncate ${
                  !isCancelled && category === 'not_started' ? 'text-muted-foreground' : 'text-white'
                }`}
                style={{ paddingLeft: isFirst ? 0 : arrow, paddingRight: isLast ? 0 : arrow }}
              >
                {getShortName(m.name)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Shorten milestone name for progress bar label */
function getShortName(name: string): string {
  // Remove common prefixes and shorten
  const short = name
    .replace(/^(Coleta de |Envio do |Envio da |Autorização de )/, '')
    .replace(/ — .*$/, '')
    .replace(/ \(.*\)$/, '');
  return short.length > 14 ? short.slice(0, 12) + '…' : short;
}
